import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import Toast, { ToastItem, ToastType } from '../components/Toast'
import Confirm from '../components/Confirm'

interface ConfirmOptions {
  mensagem: string
  detalhe?: string
  labelConfirm?: string
  labelCancel?: string
  tipo?: 'danger' | 'warning' | 'info'
}

interface UIContextType {
  toast: (message: string, type?: ToastType, duration?: number) => void
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const UIContext = createContext<UIContextType>({} as UIContextType)

export function UIProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & {
    resolve: (v: boolean) => void
  }) | null>(null)

  const toast = useCallback((message: string, type: ToastType = 'success', duration = 4000) => {
    const id = Math.random().toString(36).slice(2)
    setToasts(p => [...p, { id, type, message, duration }])
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(p => p.filter(t => t.id !== id))
  }, [])

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      setConfirmState({ ...options, resolve })
    })
  }, [])

  const handleConfirm = () => {
    confirmState?.resolve(true)
    setConfirmState(null)
  }

  const handleCancel = () => {
    confirmState?.resolve(false)
    setConfirmState(null)
  }

  return (
    <UIContext.Provider value={{ toast, confirm }}>
      {children}
      <Toast toasts={toasts} onRemove={removeToast} />
      {confirmState && (
        <Confirm
          mensagem={confirmState.mensagem}
          detalhe={confirmState.detalhe}
          labelConfirm={confirmState.labelConfirm}
          labelCancel={confirmState.labelCancel}
          tipo={confirmState.tipo}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </UIContext.Provider>
  )
}

export const useUI = () => useContext(UIContext)
